import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import "../ConvertPage/convert.css";
import "./buySellPage.css";
import Footer from "../../../customComponents/Footer";
import FiatHoldBanner, {
  useFiatHold,
} from "../../../customComponents/FiatHoldBanner";
import { SocketContext } from "../../../customComponents/SocketContext";
import {
  fetchConvertAssets,
  fetchConvertRates,
  createConvertQuote,
  executeConvertTrade,
  fetchFiatLimits,
  parseFiatConvertLimits,
  fetchSpotBalance,
  formatAedAmount,
} from "../../../api/services/fiatService";
import {
  computeConvertPreview,
  formatLiveRateLine,
} from "../../../api/services/convertCalc";
import {
  isPositiveMoneyString,
  moneyGreaterThan,
  newIdempotencyKey,
} from "../../../api/services/fiatMoney";
import {
  alertErrorMessage,
  alertSuccessMessage,
} from "../../../customComponents/CustomAlertMessage";
import {
  FALLBACK_CATALOG,
  normalizeCatalog,
  cryptosForFiat,
  fiatsForCrypto,
  sanitizeAmountInput,
  formatQuoteAmount,
  quoteMidLine,
  quoteFeeLabel,
} from "./convertCatalog";
import ConvertHistory from "./ConvertHistory";

/** REST fallback poll when convert:rates socket is down */
const RATES_POLL_MS = 2000;
const QUOTE_DEBOUNCE_MS = 400;
/** Silent firm-quote refresh while amount is set */
const QUOTE_REFRESH_MS = 1000;

function readToken() {
  try {
    return localStorage.getItem("token") || sessionStorage.getItem("token");
  } catch {
    return null;
  }
}

function AedSymbolIcon({ size = 14, color = "#ffffff", className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill={color}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M 24 14 H 54 C 76 14 90 28 90 50 C 90 72 76 86 54 86 H 24 V 14 Z M 37 25 V 75 H 52 C 69 75 78 65 78 50 C 78 35 69 25 52 25 H 37 Z" />
      <path d="M 16 35 C 28 30 42 36 56 36 H 82 C 86 36 89 34 92 33 V 41 C 88 43 84 44 80 44 H 55 C 41 44 28 39 16 43 V 35 Z" />
      <path d="M 16 52 C 28 47 42 53 56 53 H 82 C 86 53 89 51 92 50 V 58 C 88 60 84 61 80 61 H 55 C 41 61 28 56 16 60 V 52 Z" />
    </svg>
  );
}

function FiatPillIcon({ code, size }) {
  const lg = size === "lg";
  return (
    <span
      className={`buy-sell-page__fiatCircle${lg ? " buy-sell-page__fiatCircle--lg" : ""}`}
      style={{ background: "#c9a227" }}
      aria-hidden
    >
      {code === "AED" ? (
        <AedSymbolIcon size={lg ? 18 : 13} color="#ffffff" />
      ) : (
        <span className="buy-sell-page__fiatCircleText">
          {String(code || "").slice(0, 2)}
        </span>
      )}
    </span>
  );
}

function CryptoPillIcon({ icon, size }) {
  const lg = size === "lg";
  return (
    <img
      className={`buy-sell-page__cryptoPillIcon${lg ? " buy-sell-page__cryptoPillIcon--lg" : ""}`}
      src={icon}
      alt=""
      aria-hidden
    />
  );
}

function AssetPicker({
  wrapRef,
  open,
  onToggle,
  label,
  inputId,
  amount,
  onAmountChange,
  amountReadOnly,
  mirrored,
  placeholder,
  selected,
  options,
  search,
  onSearch,
  isFiat,
  isMobile,
  emptyLabel,
}) {
  const filtered = (options || []).filter((o) => {
    const q = String(search || "")
      .trim()
      .toLowerCase();
    if (!q) return true;
    return (
      o.code.toLowerCase().includes(q) ||
      String(o.name || "")
        .toLowerCase()
        .includes(q)
    );
  });

  return (
    <div
      ref={wrapRef}
      className={`buy-sell-page__field${open ? " buy-sell-page__field--spendFiatOpen" : ""}${mirrored ? " is-mirrored" : ""
        }`}
    >
      <label className="buy-sell-page__fieldLbl" htmlFor={inputId}>
        {label}
      </label>
      <div className="buy-sell-page__fieldRow">
        <input
          id={inputId}
          type="text"
          className="buy-sell-page__input"
          placeholder={placeholder}
          inputMode="decimal"
          value={amount}
          readOnly={amountReadOnly}
          onChange={
            amountReadOnly
              ? undefined
              : (e) => onAmountChange(sanitizeAmountInput(e.target.value))
          }
        />
        <button
          type="button"
          className="buy-sell-page__assetPill buy-sell-page__assetPill--btn"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={onToggle}
        >
          {isFiat ? (
            <FiatPillIcon code={selected?.code} />
          ) : (
            <CryptoPillIcon icon={selected?.icon} />
          )}
          <span className="buy-sell-page__pillCode">
            {selected?.code || "—"}
          </span>
          <i
            className={`ri-arrow-down-s-line buy-sell-page__pillCaret${open ? " buy-sell-page__pillCaret--open" : ""}`}
            aria-hidden
          />
        </button>
      </div>
      {open && !isMobile ? (
        <div
          className="buy-sell-page__fiatDropdown"
          role="listbox"
          aria-label={label}
        >
          <div className="buy-sell-page__fiatSearch">
            <i
              className="ri-search-line buy-sell-page__fiatSearchIcon"
              aria-hidden
            />
            <input
              type="search"
              className="buy-sell-page__fiatSearchInput"
              placeholder="Search"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
          <ul className="buy-sell-page__fiatList">
            {filtered.length === 0 ? (
              <li className="buy-sell-page__fiatEmpty">{emptyLabel}</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.code} role="none">
                  <button
                    type="button"
                    className="buy-sell-page__fiatOption"
                    role="option"
                    aria-selected={selected?.code === opt.code}
                    onClick={() => {
                      onToggle(opt);
                      onSearch("");
                    }}
                  >
                    {isFiat ? (
                      <FiatPillIcon code={opt.code} size="lg" />
                    ) : (
                      <CryptoPillIcon icon={opt.icon} size="lg" />
                    )}
                    <span className="buy-sell-page__fiatOptionMeta">
                      <span className="buy-sell-page__fiatOptionCode">
                        {opt.code}
                      </span>
                      <span className="buy-sell-page__fiatOptionName">
                        {opt.name}
                      </span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function QuoteReviewModal({
  open,
  quote,
  fiat,
  crypto,
  executing,
  executeError,
  onClose,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && !executing) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, executing]);

  if (!open || !quote) return null;

  const blocked = executing;

  return (
    <div
      className="buy-sell-page__payOverlay"
      role="presentation"
      onMouseDown={(e) => {
        if (executing) return;
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="buy-sell-page__payDialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="buy-sell-quote-title"
      >
        <div className="buy-sell-page__payHead">
          <h3 id="buy-sell-quote-title" className="buy-sell-page__payTitle">
            Confirm convert
          </h3>
          <button
            type="button"
            className="buy-sell-page__payClose"
            onClick={onClose}
            aria-label="Close"
            disabled={executing}
          >
            ×
          </button>
        </div>
        <div className="buy-sell-page__payBody">
          <dl className="buy-sell-page__quoteList">
            <div>
              <dt>Mid</dt>
              <dd>{quoteMidLine(quote, fiat?.code, crypto?.code) || "—"}</dd>
            </div>
            <div>
              <dt>Fee</dt>
              <dd>{quoteFeeLabel(quote, formatAedAmount)}</dd>
            </div>
            <div>
              <dt>You receive</dt>
              <dd>
                {formatQuoteAmount(
                  quote.you_receive,
                  quote.side === "BUY"
                    ? crypto?.qty_decimals
                    : fiat?.qty_decimals,
                )}{" "}
                {quote.side === "BUY" ? crypto?.code : fiat?.code}
              </dd>
            </div>
            <div>
              <dt>You spend</dt>
              <dd>
                {formatQuoteAmount(
                  quote.you_spend,
                  quote.side === "BUY"
                    ? fiat?.qty_decimals
                    : crypto?.qty_decimals,
                )}{" "}
                {quote.side === "BUY" ? fiat?.code : crypto?.code}
              </dd>
            </div>
            <div>
              <dt>Rate</dt>
              <dd>
                1 {crypto?.code} ≈ {quote.cmc_rate || quote.user_rate}{" "}
                {fiat?.code}
              </dd>
            </div>
          </dl>
          {executeError ? (
            <p className="buy-sell-page__quoteError">{executeError}</p>
          ) : null}
          <p className="buy-sell-page__quoteHint">
            Confirm spends from your spot wallet immediately. Only this confirm
            creates a history row.
          </p>
        </div>
        <div className="buy-sell-page__payFoot">
          <button
            type="button"
            className="buy-sell-page__payConfirm"
            onClick={onConfirm}
            disabled={blocked}
          >
            {executing ? "Converting…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BuySellPage() {
  const [searchParams] = useSearchParams();
  const hold = useFiatHold();
  const {
    convertRates: socketConvertRates,
    isConnected: socketConnected,
    subscribeToConvertRates,
    unsubscribeFromConvertRates,
  } = useContext(SocketContext) || {};
  const [loggedIn, setLoggedIn] = useState(() => !!readToken());
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [catalogError, setCatalogError] = useState("");
  const [side, setSide] = useState(() =>
    String(searchParams.get("side") || "").toLowerCase() === "sell"
      ? "sell"
      : "buy",
  );
  const [fiatCode, setFiatCode] = useState("AED");
  const [cryptoCode, setCryptoCode] = useState(() =>
    String(searchParams.get("asset") || "USDT").toUpperCase(),
  );
  const [amount, setAmount] = useState("");
  /** Which field the user last typed in — drives bidirectional convert math */
  const [inputField, setInputField] = useState("spend"); // 'spend' | 'receive'
  const [rates, setRates] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteKey, setQuoteKey] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState("");
  const [convertLimits, setConvertLimits] = useState({
    minAed: null,
    maxAed: null,
  });
  const [spotBalance, setSpotBalance] = useState(null);
  const [balanceTick, setBalanceTick] = useState(0);
  const [historyTick, setHistoryTick] = useState(0);

  const [spendOpen, setSpendOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [spendSearch, setSpendSearch] = useState("");
  const [receiveSearch, setReceiveSearch] = useState("");
  const spendWrapRef = useRef(null);
  const receiveWrapRef = useRef(null);
  const quoteSeq = useRef(0);
  const executeKeyRef = useRef(null);
  const quoteRef = useRef(null);
  const [isMobilePicker, setIsMobilePicker] = useState(false);

  const isBuy = side === "buy";
  quoteRef.current = quote;
  const fiatOptions = useMemo(
    () => fiatsForCrypto(catalog, cryptoCode),
    [catalog, cryptoCode],
  );
  const cryptoOptions = useMemo(
    () => cryptosForFiat(catalog, fiatCode),
    [catalog, fiatCode],
  );
  const fiat =
    fiatOptions.find((f) => f.code === fiatCode) ||
    catalog.fiat[0] ||
    FALLBACK_CATALOG.fiat[0];
  const crypto =
    cryptoOptions.find((c) => c.code === cryptoCode) ||
    catalog.crypto[0] ||
    FALLBACK_CATALOG.crypto[0];

  const amountKey = useMemo(
    () =>
      `${isBuy ? "BUY" : "SELL"}|${crypto?.code || ""}|${fiat?.code || ""}|${inputField}|${String(amount || "").trim()}`,
    [isBuy, crypto?.code, fiat?.code, inputField, amount],
  );

  const preview = useMemo(
    () =>
      computeConvertPreview({
        side: isBuy ? "BUY" : "SELL",
        amount,
        rates,
        baseAsset: crypto?.code,
        quoteAsset: fiat?.code,
        qtyDecimals: crypto?.qty_decimals,
        inputField,
      }),
    [
      isBuy,
      amount,
      rates,
      crypto?.code,
      crypto?.qty_decimals,
      fiat?.code,
      inputField,
    ],
  );

  const liveRate = useMemo(
    () => formatLiveRateLine(rates, crypto?.code, fiat?.code),
    [rates, crypto?.code, fiat?.code],
  );

  /** Prefer server quote for confirm when it matches the current amount; else local preview for display. */
  const displayQuote = quote && quoteKey === amountKey ? quote : preview;
  const quoteReady = !!(quote?.id && quoteKey === amountKey);

  useEffect(() => {
    const s = String(searchParams.get("side") || "").toLowerCase();
    if (s === "sell") setSide("sell");
    if (s === "buy") setSide("buy");
    const asset = String(searchParams.get("asset") || "").toUpperCase();
    if (asset) setCryptoCode(asset);
  }, [searchParams]);

  useEffect(() => {
    const sync = () => setLoggedIn(!!readToken());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setIsMobilePicker(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!loggedIn) {
        setCatalog(FALLBACK_CATALOG);
        return;
      }
      const [assetsRes, limitsRes] = await Promise.all([
        fetchConvertAssets(),
        fetchFiatLimits(),
      ]);
      if (cancelled) return;
      if (assetsRes.ok) {
        setCatalog(normalizeCatalog(assetsRes.assets));
        setCatalogError("");
      } else {
        setCatalog(FALLBACK_CATALOG);
        setCatalogError(
          assetsRes.error?.message || "Could not load convert assets.",
        );
      }
      if (limitsRes.ok) {
        setConvertLimits(parseFiatConvertLimits(limitsRes.limits));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loggedIn]);

  useEffect(() => {
    if (!fiatOptions.some((f) => f.code === fiatCode) && fiatOptions[0]) {
      setFiatCode(fiatOptions[0].code);
    }
  }, [fiatOptions, fiatCode]);

  useEffect(() => {
    if (!cryptoOptions.some((c) => c.code === cryptoCode) && cryptoOptions[0]) {
      setCryptoCode(cryptoOptions[0].code);
    }
  }, [cryptoOptions, cryptoCode]);

  useEffect(() => {
    if (!loggedIn) {
      setSpotBalance(null);
      return undefined;
    }
    let cancelled = false;
    const code = isBuy ? fiat?.code : crypto?.code;
    if (!code) return undefined;
    (async () => {
      const res = await fetchSpotBalance(code);
      if (!cancelled && res.ok) setSpotBalance(res.balance);
    })();
    return () => {
      cancelled = true;
    };
  }, [loggedIn, isBuy, fiat?.code, crypto?.code, balanceTick]);

  useEffect(() => {
    setQuote(null);
    setQuoteKey("");
    setQuoteError(null);
    setReviewOpen(false);
    setExecuteError("");
    executeKeyRef.current = null;
  }, [isBuy, fiat?.code, crypto?.code]);

  useEffect(() => {
    if (!loggedIn) {
      setRates(null);
      if (typeof unsubscribeFromConvertRates === "function")
        unsubscribeFromConvertRates();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const res = await fetchConvertRates();
      if (!cancelled && res.ok) setRates(res.rates);
    })();
    if (typeof subscribeToConvertRates === "function")
      subscribeToConvertRates();
    return () => {
      cancelled = true;
      if (typeof unsubscribeFromConvertRates === "function")
        unsubscribeFromConvertRates();
    };
  }, [loggedIn, subscribeToConvertRates, unsubscribeFromConvertRates]);

  useEffect(() => {
    if (socketConvertRates) setRates(socketConvertRates);
  }, [socketConvertRates]);

  useEffect(() => {
    if (!loggedIn || socketConnected) return undefined;
    const id = setInterval(() => {
      void (async () => {
        const res = await fetchConvertRates();
        if (res.ok) setRates(res.rates);
      })();
    }, RATES_POLL_MS);
    return () => clearInterval(id);
  }, [loggedIn, socketConnected]);

  useEffect(() => {
    if (!isMobilePicker) return undefined;
    if (!spendOpen && !receiveOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSpendOpen(false);
        setReceiveOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [isMobilePicker, spendOpen, receiveOpen]);

  useEffect(() => {
    if (isMobilePicker || (!spendOpen && !receiveOpen)) return undefined;
    const onPointerDown = (e) => {
      if (spendWrapRef.current && !spendWrapRef.current.contains(e.target))
        setSpendOpen(false);
      if (receiveWrapRef.current && !receiveWrapRef.current.contains(e.target))
        setReceiveOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isMobilePicker, spendOpen, receiveOpen]);

  const requestQuote = useCallback(
    async (opts = {}) => {
      const silent = !!opts.silent;
      if (
        !loggedIn ||
        hold.held ||
        !isPositiveMoneyString(amount) ||
        !fiat?.code ||
        !crypto?.code
      ) {
        setQuote(null);
        setQuoteKey("");
        setQuoting(false);
        return { ok: false, quote: null };
      }
      // Firm quote always uses spend-side amount the API expects (AED on BUY, crypto on SELL).
      // When the user typed receive, local preview inverts to you_spend first.
      const local = computeConvertPreview({
        side: isBuy ? "BUY" : "SELL",
        amount,
        rates,
        baseAsset: crypto.code,
        quoteAsset: fiat.code,
        qtyDecimals: crypto.qty_decimals,
        inputField,
      });
      const spendForApi = local?.you_spend;
      if (!isPositiveMoneyString(spendForApi)) {
        if (!silent) {
          setQuote(null);
          setQuoteKey("");
          setQuoting(false);
        }
        return { ok: false, quote: null };
      }
      const key = `${isBuy ? "BUY" : "SELL"}|${crypto.code}|${fiat.code}|${inputField}|${String(amount).trim()}`;
      const seq = ++quoteSeq.current;
      if (!silent) setQuoting(true);
      const body = isBuy
        ? {
          side: "BUY",
          base_asset: crypto.code,
          quote_asset: fiat.code,
          amount_aed: spendForApi,
        }
        : {
          side: "SELL",
          base_asset: crypto.code,
          quote_asset: fiat.code,
          amount_crypto: spendForApi,
        };
      const res = await createConvertQuote(body);
      if (seq !== quoteSeq.current)
        return { ok: false, quote: null, stale: true };
      if (!silent) setQuoting(false);
      if (!res.ok) {
        // Keep last good quote on silent refresh failures; clear only on user-driven requests
        if (!silent) {
          setQuote(null);
          setQuoteKey("");
          setQuoteError(res.error);
        }
        return { ok: false, quote: null, error: res.error };
      }
      setQuote(res.quote);
      setQuoteKey(key);
      setQuoteError(null);
      executeKeyRef.current = null;
      return { ok: true, quote: res.quote };
    },
    [
      loggedIn,
      hold.held,
      amount,
      inputField,
      isBuy,
      fiat?.code,
      crypto?.code,
      crypto?.qty_decimals,
      rates,
    ],
  );

  useEffect(() => {
    if (!loggedIn || hold.held || !isPositiveMoneyString(amount)) {
      setQuote(null);
      setQuoteKey("");
      setQuoting(false);
      setQuoteError(null);
      return undefined;
    }
    const t = setTimeout(() => {
      void requestQuote();
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      quoteSeq.current += 1;
    };
  }, [
    loggedIn,
    hold.held,
    amount,
    inputField,
    isBuy,
    fiat?.code,
    crypto?.code,
    refreshNonce,
    preview?.cmc_rate,
    preview?.you_spend,
    requestQuote,
  ]);

  // Silently refresh firm quote every 1s while amount is set (numbers only — no status text)
  useEffect(() => {
    if (!loggedIn || hold.held || !isPositiveMoneyString(amount))
      return undefined;
    const id = setInterval(() => {
      void requestQuote({ silent: true });
    }, QUOTE_REFRESH_MS);
    return () => clearInterval(id);
  }, [
    loggedIn,
    hold.held,
    amount,
    inputField,
    isBuy,
    fiat?.code,
    crypto?.code,
    requestQuote,
  ]);

  const closePickers = () => {
    setSpendOpen(false);
    setReceiveOpen(false);
    setSpendSearch("");
    setReceiveSearch("");
  };

  const handleExecute = useCallback(async () => {
    if (!quote?.id || executing || hold.held) return;
    setExecuting(true);
    setExecuteError("");
    if (!executeKeyRef.current) executeKeyRef.current = newIdempotencyKey("cv");
    const key = executeKeyRef.current;
    const res = await executeConvertTrade(quote.id, key);
    setExecuting(false);
    if (!res.ok) {
      const code = res.error?.code;
      const message = res.error?.message || "Convert failed.";
      setExecuteError(message);
      alertErrorMessage(message);
      if (code === "PRICE_MOVED" || code === "QUOTE_EXPIRED") {
        setReviewOpen(false);
        executeKeyRef.current = null;
        setQuote(null);
        setQuoteKey("");
        setRefreshNonce((n) => n + 1);
      } else if (code === "INVENTORY_LOW") {
        setReviewOpen(false);
        executeKeyRef.current = null;
        setQuote(null);
        setQuoteKey("");
      } else if (code === "INSUFFICIENT_BALANCE") {
        setReviewOpen(false);
        executeKeyRef.current = null;
      } else if (code === "QUOTE_ALREADY_EXECUTED") {
        setReviewOpen(false);
        executeKeyRef.current = null;
        setQuote(null);
        setQuoteKey("");
        setAmount("");
        setInputField("spend");
        setHistoryTick((n) => n + 1);
        setBalanceTick((n) => n + 1);
      }
      return;
    }
    alertSuccessMessage("Convert executed.");
    executeKeyRef.current = null;
    setReviewOpen(false);
    setExecuteError("");
    setQuote(null);
    setQuoteKey("");
    setAmount("");
    setInputField("spend");
    setHistoryTick((n) => n + 1);
    setBalanceTick((n) => n + 1);
  }, [quote?.id, executing, hold.held]);

  const spendAsset = isBuy ? fiat : crypto;
  const receiveAsset = isBuy ? crypto : fiat;
  const spendOptions = isBuy ? fiatOptions : cryptoOptions;
  const receiveOptions = isBuy ? cryptoOptions : fiatOptions;

  const onSpendAmountChange = useCallback((raw) => {
    setInputField("spend");
    setAmount(raw);
  }, []);

  const onReceiveAmountChange = useCallback((raw) => {
    setInputField("receive");
    setAmount(raw);
  }, []);

  // Edited field shows raw input; the other field mirrors live preview / firm quote.
  const spendAmountValue =
    inputField === "spend"
      ? amount
      : displayQuote?.you_spend
        ? String(displayQuote.you_spend)
        : "";
  const receiveAmountValue =
    inputField === "receive"
      ? amount
      : displayQuote?.you_receive
        ? String(displayQuote.you_receive)
        : "";
  const pageTitle = isBuy
    ? `Buy ${crypto?.code || "USDT"} with ${fiat?.code || "AED"}`
    : `Sell ${crypto?.code || "USDT"} for ${fiat?.code || "AED"}`;

  // Spend that will leave the wallet (preview / firm quote you_spend)
  const spendForBalance =
    displayQuote?.you_spend || (inputField === "spend" ? amount : "");
  const insufficientBalance =
    loggedIn &&
    spotBalance != null &&
    isPositiveMoneyString(spendForBalance) &&
    moneyGreaterThan(spendForBalance, spotBalance);

  const canReview =
    loggedIn &&
    !hold.held &&
    !executing &&
    isPositiveMoneyString(amount) &&
    !!displayQuote &&
    !insufficientBalance;

  const openConfirm = useCallback(async () => {
    if (!loggedIn || hold.held || executing || !isPositiveMoneyString(amount))
      return;
    if (insufficientBalance) {
      alertErrorMessage(
        `Insufficient ${spendAsset?.code || "balance"}. Spot available: ${formatQuoteAmount(
          spotBalance,
          spendAsset?.qty_decimals,
        )} ${spendAsset?.code || ""}`.trim(),
      );
      return;
    }
    setExecuteError("");
    if (quoteReady && quote?.id) {
      setReviewOpen(true);
      return;
    }
    const res = await requestQuote({ silent: true });
    if (res?.ok && res.quote?.id) {
      setReviewOpen(true);
      return;
    }
    if (res?.error?.message) {
      alertErrorMessage(res.error.message);
    } else if (!res?.stale) {
      alertErrorMessage("Could not get a live quote. Try again.");
    }
  }, [
    loggedIn,
    hold.held,
    executing,
    amount,
    insufficientBalance,
    spotBalance,
    spendAsset?.code,
    spendAsset?.qty_decimals,
    quoteReady,
    quote?.id,
    requestQuote,
  ]);

  const cta = (() => {
    if (!loggedIn) {
      return (
        <Link to="/login" className="buy-sell-page__cta">
          Log in to convert
        </Link>
      );
    }
    if (hold.held) {
      return (
        <button type="button" className="buy-sell-page__cta" disabled>
          Fiat paused
        </button>
      );
    }
    if (!isPositiveMoneyString(amount)) {
      return (
        <button type="button" className="buy-sell-page__cta" disabled>
          Enter amount
        </button>
      );
    }
    if (insufficientBalance) {
      return (
        <button type="button" className="buy-sell-page__cta" disabled>
          Insufficient balance
        </button>
      );
    }
    if (quoteError && !displayQuote) {
      return (
        <button
          type="button"
          className="buy-sell-page__cta"
          onClick={() => setRefreshNonce((n) => n + 1)}
        >
          Try again
        </button>
      );
    }
    return (
      <button
        type="button"
        className="buy-sell-page__cta"
        disabled={!canReview}
        onClick={() => {
          void openConfirm();
        }}
      >
        Confirm
      </button>
    );
  })();

  const pickerSheetOpen =
    isMobilePicker &&
    typeof document !== "undefined" &&
    (spendOpen || receiveOpen);

  return (
    <>
      <main className="buy-sell-page">
        <div className="buy-sell-page__container">
          <FiatHoldBanner />
          <div className="buy-sell-page__mainGrid">
            <section
              className="buy-sell-page__card buy-sell-page__card--trade"
              aria-label="Convert quote"
            >
              <div className="buy-sell-page__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isBuy}
                  className={`buy-sell-page__tab buybtn_lft${isBuy ? " buy-sell-page__tab--active" : ""}`}
                  onClick={() => {
                    setSide("buy");
                    setAmount("");
                    setInputField("spend");
                    closePickers();
                  }}
                >
                  Buy
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isBuy}
                  className={`buy-sell-page__tab${!isBuy ? " buy-sell-page__tab--active" : ""}`}
                  onClick={() => {
                    setSide("sell");
                    setAmount("");
                    setInputField("spend");
                    closePickers();
                  }}
                >
                  Sell
                </button>
              </div>

              <div className="buysell_mid_sl">
                <AssetPicker
                  wrapRef={spendWrapRef}
                  open={spendOpen}
                  onToggle={(opt) => {
                    if (opt && opt.code) {
                      if (isBuy) setFiatCode(opt.code);
                      else setCryptoCode(opt.code);
                      setSpendOpen(false);
                      return;
                    }
                    setReceiveOpen(false);
                    setSpendOpen((v) => !v);
                  }}
                  label={isBuy ? "You spend" : "You sell"}
                  inputId="buy-sell-spend"
                  amount={spendAmountValue}
                  onAmountChange={onSpendAmountChange}
                  mirrored={inputField === "receive" && !!spendAmountValue}
                  placeholder="0.00"
                  selected={spendAsset}
                  options={spendOptions}
                  search={spendSearch}
                  onSearch={setSpendSearch}
                  isFiat={isBuy}
                  isMobile={isMobilePicker}
                  emptyLabel={isBuy ? "No currencies found" : "No assets found"}
                />

                <AssetPicker
                  wrapRef={receiveWrapRef}
                  open={receiveOpen}
                  onToggle={(opt) => {
                    if (opt && opt.code) {
                      if (isBuy) setCryptoCode(opt.code);
                      else setFiatCode(opt.code);
                      setReceiveOpen(false);
                      return;
                    }
                    setSpendOpen(false);
                    setReceiveOpen((v) => !v);
                  }}
                  label="You receive (est.)"
                  inputId="buy-sell-receive"
                  amount={receiveAmountValue}
                  onAmountChange={onReceiveAmountChange}
                  mirrored={inputField === "spend" && !!receiveAmountValue}
                  placeholder="0.00"
                  selected={receiveAsset}
                  options={receiveOptions}
                  search={receiveSearch}
                  onSearch={setReceiveSearch}
                  isFiat={!isBuy}
                  isMobile={isMobilePicker}
                  emptyLabel={isBuy ? "No assets found" : "No currencies found"}
                />

                <div className="buy-sell-page__hintsGrid" aria-live="polite">
                  <div className="buy-sell-page__hintsCol">
                    {loggedIn && spotBalance != null ? (
                      <p
                        className={`buy-sell-page__balanceHint${insufficientBalance ? " buy-sell-page__balanceHint--error" : ""}`}
                      >
                        <i
                          className="ri-wallet-3-line buy-sell-page__hintIcon"
                          aria-hidden
                        />{" "}
                        Spot available:{" "}
                        {formatQuoteAmount(spotBalance, spendAsset?.qty_decimals)}{" "}
                        {spendAsset?.code}
                        {insufficientBalance ? " · Not enough" : ""}
                      </p>
                    ) : (
                      <p className="buy-sell-page__balanceHint buy-sell-page__balanceHint--placeholder">
                        <i
                          className="ri-wallet-3-line buy-sell-page__hintIcon"
                          aria-hidden
                        />{" "}
                        {loggedIn
                          ? "Loading spot balance…"
                          : "Log in to see spot balance"}
                        &nbsp;•&nbsp; <span className="buy-sell-page__liveDot" />{" "}
                        <strong className="buy-sell-page__liveTxt">Live</strong>
                      </p>
                    )}
                    {isBuy &&
                      convertLimits.minAed != null &&
                      convertLimits.minAed > 0 ? (
                      <p className="buy-sell-page__limitHint">
                        <i
                          className="ri-file-list-line buy-sell-page__hintIcon"
                          aria-hidden
                        />{" "}
                        Minimum transaction:{" "}
                        {formatAedAmount(convertLimits.minAed)} AED
                        {convertLimits.maxAed != null
                          ? ` · ${formatAedAmount(convertLimits.maxAed)} max`
                          : ""}
                      </p>
                    ) : isBuy ? (
                      <p className="buy-sell-page__limitHint buy-sell-page__limitHint--placeholder">
                        <i
                          className="ri-file-list-line buy-sell-page__hintIcon"
                          aria-hidden
                        />{" "}
                        Minimum transaction: 100.00 AED
                      </p>
                    ) : null}
                  </div>
                  <div className="buy-sell-page__hintsCol buy-sell-page__hintsCol--right">
                    <p>
                      <span className="buy-sell-page__liveDot" />{" "}
                      <strong className="buy-sell-page__liveTxt">Live</strong>
                    </p>
                    <p className="buy-sell-page__quoteTimer">
                      Quote valid for 30s &nbsp;
                      <i
                        className="ri-refresh-line buy-sell-page__refreshBtn"
                        onClick={() => setRefreshNonce((n) => n + 1)}
                        title="Refresh rate"
                      />
                    </p>
                  </div>
                </div>

                {/* Always-mounted quote panel */}
                <div
                  className={[
                    "buy-sell-page__quoteMeta",
                    displayQuote ? "" : "is-empty",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-live="polite"
                >
                  <div className="buy-sell-page__quoteSummaryTitle">
                    Quote summary
                  </div>

                  <div className="buy-sell-page__quoteRow">
                    <span>Market rate</span>
                    <strong>
                      {displayQuote
                        ? `1 ${crypto?.code || ""} ≈ ${displayQuote.cmc_rate || displayQuote.user_rate} ${fiat?.code || ""}`
                        : liveRate?.text ||
                        `1 ${crypto?.code || "USDT"} ≈ 3.668 ${fiat?.code || "AED"}`}
                    </strong>
                  </div>

                  <div className="buy-sell-page__quoteRow">
                    <span>
                      Fee{" "}
                      <i
                        className="ri-information-line buy-sell-page__infoIcon"
                        title="Flat fee or tier catalog rate"
                      />
                    </span>
                    <strong>
                      {displayQuote
                        ? quoteFeeLabel(displayQuote, formatAedAmount)
                        : "0.00 AED"}
                    </strong>
                  </div>

                  <div className="buy-sell-page__quoteDivider" />

                  <div className="buy-sell-page__quoteRow">
                    <span>You receive</span>
                    <strong className="buy-sell-page__greenVal">
                      {displayQuote
                        ? `${formatQuoteAmount(displayQuote.you_receive, receiveAsset?.qty_decimals)} ${receiveAsset?.code || ""}`.trim()
                        : `0.00 ${receiveAsset?.code || "USDT"}`}
                    </strong>
                  </div>

                  <div className="buy-sell-page__quoteRow">
                    <span>You spend</span>
                    <strong>
                      {displayQuote
                        ? `${formatQuoteAmount(displayQuote.you_spend, spendAsset?.qty_decimals)} ${spendAsset?.code || ""}`.trim()
                        : `0.00 ${spendAsset?.code || "AED"}`}
                    </strong>
                  </div>

                  <div className="buy-sell-page__quoteRow">
                    <span>Exchange rate (after fee)</span>
                    <strong>
                      {displayQuote
                        ? quoteMidLine(displayQuote, fiat?.code, crypto?.code) ||
                        "—"
                        : "—"}
                    </strong>
                  </div>
                </div>

                {catalogError ? (
                  <p className="buy-sell-page__quoteError">{catalogError}</p>
                ) : null}
                {quoteError && !displayQuote ? (
                  <p className="buy-sell-page__quoteError">
                    {quoteError.message}
                  </p>
                ) : null}

                {cta}

                <p className="buy-sell-page__footerGuarantee">
                  <i className="ri-shield-check-line" aria-hidden /> Secure
                  conversion &nbsp;•&nbsp; Final rate shown before confirmation
                </p>
              </div>
            </section>

            <section
              className={`buy-sell-page__card buy-sell-page__card--hot buy-sell-page__card--${isBuy ? "buy" : "sell"}`}
              aria-labelledby="buy-sell-hot-title"
            >
              <h1 className="buy-sell-page__pageTitle">
                <span className="buy-sell-page__titleHighlight">
                  {isBuy ? "Buy" : "Sell"}
                </span>{" "}
                {isBuy
                  ? `${crypto?.code || "USDT"} with ${fiat?.code || "AED"}`
                  : `${crypto?.code || "USDT"} for ${fiat?.code || "AED"}`}
              </h1>
              <p className="buy-sell-page__subtitle">
                {isBuy
                  ? `Convert ${fiat?.code || "AED"} to ${crypto?.code || "USDT"} instantly at the best market rate.`
                  : `Convert ${crypto?.code || "USDT"} to ${fiat?.code || "AED"} instantly at the best market rate.`}
              </p>

              <div className="buy-sell-page__badgeBar">
                <div className="buy-sell-page__badge">
                  <i
                    className="ri-wallet-3-line buy-sell-page__badgeIcon"
                    aria-hidden
                  />
                  <span>SPOT</span>
                </div>
                <div className="buy-sell-page__badge">
                  <FiatPillIcon code={fiat?.code} />
                  <span>{fiat?.code || "AED"}</span>
                </div>
                <div className="buy-sell-page__badge">
                  <CryptoPillIcon icon={crypto?.icon} />
                  <span>{crypto?.code || "USDT"}</span>
                </div>
              </div>

              <div className="buy-sell-page__stepper">
                <div className="buy-sell-page__step">
                  <div className="buy-sell-page__stepIconWrap">
                    <div className="buy-sell-page__stepDiamond">
                      <span>1</span>
                    </div>
                    <div className="buy-sell-page__stepLine" />
                  </div>
                  <div className="buy-sell-page__stepContent">
                    <h3 className="buy-sell-page__stepTitle">Enter amount</h3>
                    <p className="buy-sell-page__stepDesc">
                      Enter AED or USDT. The other field updates automatically
                      using the live rate.
                    </p>

                    <div className="buy-sell-page__featureCard">
                      {/* <div className="buy-sell-page__featureIcon buy-sell-page__featureIcon--gold">
                      <i className="ri-flashlight-line" aria-hidden />
                    </div> */}
                      <div className="buy-sell-page__featureMeta">
                        <div className="buy-sell-page__featureTitle">
                          Live rates from multiple sources
                        </div>
                        <p className="buy-sell-page__featureDesc">
                          Updated in real time
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="buy-sell-page__step">
                  <div className="buy-sell-page__stepIconWrap">
                    <div className="buy-sell-page__stepDiamond">
                      <span>2</span>
                    </div>
                    <div className="buy-sell-page__stepLine" />
                  </div>
                  <div className="buy-sell-page__stepContent">
                    <h3 className="buy-sell-page__stepTitle">Review quote</h3>
                    <p className="buy-sell-page__stepDesc">
                      Review the rate, fee, amount received, and quote expiry
                      before confirming.
                    </p>

                    <div className="buy-sell-page__featureCard">
                      {/* <div className="buy-sell-page__featureIcon buy-sell-page__featureIcon--blue">
                      <i className="ri-shield-check-line" aria-hidden />
                    </div> */}
                      <div className="buy-sell-page__featureMeta">
                        <div className="buy-sell-page__featureTitle">
                          Secure &amp; transparent
                        </div>
                        <p className="buy-sell-page__featureDesc">
                          No hidden fees. What you see is what you get.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="buy-sell-page__step">
                  <div className="buy-sell-page__stepIconWrap">
                    <div className="buy-sell-page__stepDiamond">
                      <span>3</span>
                    </div>
                  </div>
                  <div className="buy-sell-page__stepContent">
                    <h3 className="buy-sell-page__stepTitle">
                      Confirm conversion
                    </h3>
                    <p className="buy-sell-page__stepDesc">
                      Confirm to complete the conversion and credit USDT to your
                      Spot wallet.
                    </p>

                    <div className="buy-sell-page__featureCard">
                      {/* <div className="buy-sell-page__featureIcon buy-sell-page__featureIcon--green">
                      <i className="ri-checkbox-circle-line" aria-hidden />
                    </div> */}
                      <div className="buy-sell-page__featureMeta">
                        <div className="buy-sell-page__featureTitle">
                          Instant credit
                        </div>
                        <p className="buy-sell-page__featureDesc">
                          USDT will be credited to your Spot wallet instantly.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {loggedIn ? (
            <ConvertHistory loggedIn={loggedIn} reloadToken={historyTick} />
          ) : null}

          {pickerSheetOpen
            ? createPortal(
              <div
                className="buy-sell-page__currencyPickerOverlay"
                role="presentation"
                onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  if (e.target === e.currentTarget) closePickers();
                }}
              >
                <div
                  className="buy-sell-page__currencyPickerSheet"
                  role="dialog"
                  aria-modal="true"
                  aria-label={
                    spendOpen
                      ? isBuy
                        ? "Select currency"
                        : "Select asset"
                      : isBuy
                        ? "Select asset"
                        : "Select currency"
                  }
                >
                  <div
                    className="buy-sell-page__currencyPickerHandle"
                    aria-hidden
                  />
                  <div
                    className="buy-sell-page__fiatDropdown buy-sell-page__fiatDropdown--sheet"
                    role="listbox"
                  >
                    <div className="buy-sell-page__fiatSearch">
                      <i
                        className="ri-search-line buy-sell-page__fiatSearchIcon"
                        aria-hidden
                      />
                      <input
                        type="search"
                        className="buy-sell-page__fiatSearchInput"
                        placeholder="Search"
                        value={spendOpen ? spendSearch : receiveSearch}
                        onChange={(e) =>
                          spendOpen
                            ? setSpendSearch(e.target.value)
                            : setReceiveSearch(e.target.value)
                        }
                        autoComplete="off"
                      />
                    </div>
                    <ul className="buy-sell-page__fiatList">
                      {(spendOpen ? spendOptions : receiveOptions)
                        .filter((o) => {
                          const q = String(
                            spendOpen ? spendSearch : receiveSearch,
                          )
                            .trim()
                            .toLowerCase();
                          if (!q) return true;
                          return (
                            o.code.toLowerCase().includes(q) ||
                            String(o.name || "")
                              .toLowerCase()
                              .includes(q)
                          );
                        })
                        .map((opt) => {
                          const selected = spendOpen
                            ? spendAsset
                            : receiveAsset;
                          const pickIsFiat = spendOpen ? isBuy : !isBuy;
                          return (
                            <li key={opt.code} role="none">
                              <button
                                type="button"
                                className="buy-sell-page__fiatOption"
                                role="option"
                                aria-selected={selected?.code === opt.code}
                                onClick={() => {
                                  if (spendOpen) {
                                    if (isBuy) setFiatCode(opt.code);
                                    else setCryptoCode(opt.code);
                                  } else if (isBuy) setCryptoCode(opt.code);
                                  else setFiatCode(opt.code);
                                  closePickers();
                                }}
                              >
                                {pickIsFiat ? (
                                  <FiatPillIcon code={opt.code} size="lg" />
                                ) : (
                                  <CryptoPillIcon icon={opt.icon} size="lg" />
                                )}
                                <span className="buy-sell-page__fiatOptionMeta">
                                  <span className="buy-sell-page__fiatOptionCode">
                                    {opt.code}
                                  </span>
                                  <span className="buy-sell-page__fiatOptionName">
                                    {opt.name}
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </div>
              </div>,
              document.body,
            )
            : null}

          <QuoteReviewModal
            open={reviewOpen}
            quote={quote}
            fiat={fiat}
            crypto={crypto}
            executing={executing}
            executeError={executeError}
            onClose={() => {
              if (executing) return;
              setReviewOpen(false);
              setExecuteError("");
            }}
            onConfirm={handleExecute}
          />
        </div>

      </main>
      <Footer />
    </>
  );
}
